<template>
  <li
    role="treeitem"
    class="tree-node"
    :data-id="node.id"
    :class="nodeClass"
    @mousedown.stop="handleMouseDown"
  >
    <div
      class="tree-content"
      :style="[options.direction == 'ltr' ? {'padding-left': padding} : {'padding-right': padding}]"
      @click.stop="select"
    >
      <svg
          class="tree-arrow"
          :class="[{'expanded': node.states.expanded, 'has-child': node.children.length || node.isBatch}, options.direction]"
          width="5"
          height="7"
          viewBox="0 0 5 7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          @click.stop="toggleExpand"
      >
        <path d="M4.15597 3.19897L1.1634 0.580477C0.904769 0.354173 0.5 0.537844 0.5 0.881508V6.11849C0.5 6.46216 0.904768 6.64583 1.1634 6.41952L4.15596 3.80103C4.3381 3.64167 4.3381 3.35833 4.15597 3.19897Z" fill="#B6BCC8"/>
      </svg>
      <svg
          class="circle"
          :class="[{'has-child': node.children.length || node.isBatch,'checked': node.states.checked}, options.direction]"
          width="5"
          height="4"
          viewBox="0 0 5 4"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="2.5" cy="2" r="2" fill="#B6BCC8"/>
      </svg>
      <span
          ref="anchor"
          class="tree-anchor"
          tabindex="-1"
          @focus="onNodeFocus"
          @dblclick="tree.$emit('node:dblclick', node)"
      >
        <img src="../../../assets/icons/tree/folder.svg" class="node-icon" v-if="!node.states.checked">
        <img src="../../../assets/icons/tree/folder_active.svg" class="node-icon" v-else>
        <node-content :node="node" />
      </span>

      <span class="dropshipping-status" v-if="options.dropshipping">
        {{node.states.without_checkbox ? 'Нет' : 'Да'}}
      </span>
      <i
        v-if="options.checkbox && !node.states.disabled && !node.states.without_checkbox"
        class="tree-checkbox"
        :class="{'checked': node.states.checked, 'indeterminate': node.states.indeterminate}"
        @click.stop="check"
      />
    </div>
    <transition name="l-fade">
      <ul
        v-if="hasChildren() && node.states.expanded"
        class="tree-children"
      >
        <node
          v-for="child in visibleChildren"
          :key="child.id"
          :node="child"
          :options="options"
        />
      </ul>
    </transition>
  </li>
</template>

<script>
  import NodeContent from './NodeContent.vue'

  const TreeNode = {
    name: 'Node',
    inject: ['tree'],
    props: ['node', 'options'],

    components: {
      NodeContent
    },

    watch: {
      node() {
        this.node.vm = this
      }
    },

    data() {
      this.node.vm = this

      return {
        loading: false
      }
    },

    computed: {
      padding() {
        return this.node.depth * (this.options.paddingLeft ? this.options.paddingLeft : this.options.nodeIndent) + 'px'
      },

      nodeClass() {
        let state = this.node.states
        let hasChildren = this.hasChildren()
        let classes = {
          'has-child': hasChildren,
          'expanded': hasChildren && state.expanded,
          'selected': state.selected,
          'disabled': state.disabled,
          'matched': state.matched,
          'dragging': state.dragging,
          'loading': this.loading,
          'draggable': state.draggable
        }

        if (this.options.checkbox) {
          classes['checked'] = state.checked
          classes['indeterminate'] = state.indeterminate
        }

        return classes
      },

      visibleChildren() {
        return this.node.children.filter(function(child) {
          return child && child.visible()
        })
      }
    },

    methods: {
      onNodeFocus() {
        this.tree.activeElement = this.node
      },

      focus() {
        this.$refs.anchor.focus()
        this.node.select()
      },

      check() {
        if (this.node.checked()) {
          this.node.uncheck()
        } else {
          this.node.check()
        }
      },

      // eslint-disable-next-line no-undef
      select({ctrlKey} = evnt) {
        const opts = this.options
        const tree = this.tree
        const node = this.node

        tree.$emit('node:clicked', node)

        if (opts.editing && node.isEditing) {
          return
        }

        if (opts.editing && node.editable()) {
          return this.startEditing()
        }

        if (opts.checkbox && opts.checkOnSelect) {
          if (!opts.parentSelect && this.hasChildren()) {
            return this.toggleExpand()
          }

          return this.check(ctrlKey)
        }

        // 'parentSelect' behaviour.
        // For nodes which has a children list we have to expand/collapse
        if (!opts.parentSelect && this.hasChildren()) {
          this.toggleExpand()
        }

        if (opts.multiple) {
          if (!node.selected()) {
            node.select(ctrlKey)
          } else {
            if (ctrlKey) {
              node.unselect()
            } else {
              if (this.tree.selectedNodes.length != 1) {
                tree.unselectAll()
                node.select()
              }
            }
          }
        } else {
          if (node.selected() && ctrlKey) {
            node.unselect()
          } else {
            node.select()
          }
        }
      },

      toggleExpand() {
        if (this.hasChildren()) {
          this.node.toggleExpand()
        }
      },

      hasChildren() {
        return this.node.hasChildren()
      },

      startEditing() {
        if (this.tree._editingNode) {
          this.tree._editingNode.stopEditing()
        }

        this.node.startEditing()
      },

      stopEditing() {
        this.node.stopEditing()
      },

      handleMouseDown(event) {
        if (!this.options.dnd) {
          return
        }

        this.tree.vm.startDragging(this.node, event)
      }
    }
  }

  export default TreeNode
</script>

<style>
.dropshipping-status {
  font-size: 12px;
  position: absolute;
  right: 230px;
  text-align: center;
  width: 24px;
}
.node-icon{
  margin-right: 9px;
}
  .tree-node {
    white-space: nowrap;
    display: flex;
    flex-direction: column;
    position: relative;
    box-sizing: border-box;
  }

  .tree-content {
    display: flex;
    align-items: center;
    padding: 0 10px;
    cursor: pointer;
    width: 100%;
    box-sizing: border-box;
  }

  .tree-node:not(.selected) > .tree-content:hover {
    background: #f5f6f8;
  }

  .tree-node.selected > .tree-content {
    background-color: #EEF0F3;
  }

  .tree-node.disabled > .tree-content:hover {
    background: inherit;
  }

  .tree-arrow {
    flex-shrink: 0;
    cursor: pointer;
    margin-left: 10px;
    width: 0;
  }
  .circle {
    flex-shrink: 0;
    cursor: pointer;
    width: 5px;
  }

  .tree-arrow.expanded{
    transform: rotate(90deg);
  }
  .tree-arrow.expanded > path{
   fill: #2CB181;
  }

  .circle.checked > circle{
   fill: #2CB181;
  }

  .tree-arrow.has-child {
    margin-left: 10px;
    width: 5px;
    position: relative;
  }

  .circle.has-child {
    width: 0;
    position: relative;
  }

  .tree-arrow.has-child:after {
    border: 1.5px solid #494646;
    position: absolute;
    border-left: 0;
    border-top: 0;
    left: 9px;
    top: 50%;
    height: 9px;
    width: 9px;
    transform: rotate(-45deg) translateY(-50%) translateX(0);
    transition: transform .25s;
    transform-origin: center;
  }

  .tree-arrow.has-child.rtl:after {
    border: 1.5px solid #494646;
    position: absolute;
    border-right: 0;
    border-bottom: 0;
    right: 0px;
    top: 50%;
    height: 9px;
    width: 9px;
    transform: rotate(-45deg) translateY(-50%) translateX(0);
    transition: transform .25s;
    transform-origin: center;
  }

  .tree-arrow.expanded.has-child:after {
    transform: rotate(45deg) translateY(-50%) translateX(-5px);
  }

  .tree-checkbox {
    flex-shrink: 0;
    position: sticky;
    right: 10px;
    width: 20px;
    height: 20px;
    box-sizing: border-box;
    border: 1px solid #B6BCC8;
    border-radius: 4px;
    background: #fff;
    transition: border-color .3s, background-color .3s;
  }

  .tree-checkbox:after {
    position: absolute;
    display: block;
    content: "";
  }

  .tree-checkbox.checked,
  .tree-checkbox.indeterminate {
    background-color: #2CB181;
    border-color: #2CB181;
  }

  .tree-checkbox.checked:after {
    box-sizing: content-box;
    border: 2px solid #fff;
    border-left: 0;
    border-top: 0;
    left: 7px;
    top: 3px;
    height: 8px;
    width: 3px;
    transform: rotate(45deg) scaleY(0);
    transition: transform .25s;
    transform-origin: center;
  }

  .tree-checkbox.checked:after {
    transform: rotate(45deg) scaleY(1);
  }

  .tree-checkbox.indeterminate:after {
    background-color: #fff;
    top: 50%;
    left: 20%;
    right: 20%;
    height: 2px;
  }

  .tree-anchor {
    flex-grow: 2;
    outline: none;
    display: flex;
    text-decoration: none;
    color: #343434;
    vertical-align: top;
    margin-left: 0;
    line-height: 24px;
    padding: 3px 6px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .tree-anchor > span {
    /*overflow: hidden;*/
    width: 9vw;
    white-space: pre-wrap;
    text-overflow: ellipsis;
    font-family: Roboto;
    font-size: 13px;
    line-height: 15px;
  }

  .tree-node.selected > .tree-content > .tree-anchor {
    outline: none;
  }

  .tree-node.disabled > .tree-content > .tree-anchor {
    color: #989191;
    background: #fff;
    opacity: .6;
    cursor: default;
    outline: none;
  }

  .tree-input {
    display: block;
    width: 100%;
    height: 24px;
    line-height: 24px;
    outline: none;
    border: 1px solid #3498db;
    padding: 0 4px;
  }

  .l-fade-enter-active, .l-fade-leave-active {
    max-height: 100vh;
    transition: max-height .2s;
  }

  .l-fade-enter, .l-fade-leave-to {
    overflow: hidden;
    max-height: 0;
  }


  .tree--small .tree-anchor {
    line-height: 15px;
  }

  .tree--small .tree-checkbox {
    width: 12px;
    height: 12px;
  }

  .tree--small .tree-arrow {
    height: 23px;
  }

  .tree--small .tree-checkbox.checked:after {
    left: 7px;
    top: 3px;
    height: 11px;
    width: 5px;
  }

  .tree-node.has-child.loading > .tree-content > .tree-arrow,
  .tree-node.has-child.loading > .tree-content > .tree-arrow:after {
    border-radius: 50%;
    width: 15px;
    height: 15px;
    border: 0;
  }

  .tree-node.has-child.loading > .tree-content > .tree-arrow {
    font-size: 3px;
    position: relative;
    border-top: 1.1em solid rgba(45,45,45, 0.2);
    border-right: 1.1em solid rgba(45,45,45, 0.2);
    border-bottom: 1.1em solid rgba(45,45,45, 0.2);
    border-left: 1.1em solid #2d2d2d;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    left: 5px;
    -webkit-animation: loading 1.1s infinite linear;
    animation: loading 1.1s infinite linear;
    margin-right: 8px;
  }

  @-webkit-keyframes loading {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  @keyframes loading {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
@media(max-width: 1200px) {
  .tree-children .tree-content{
    padding-left: 6px!important;
  }
  .tree-anchor{
    padding: 3px 2px;
  }
  .node-icon{
    margin-right: 2px;
  }
}
</style>
